import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FaCog, 
  FaBell, 
  FaShieldAlt, 
  FaPalette,
  FaSave,
  FaTimes,
  FaEye,
  FaEyeSlash,
  FaKey
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  color: #141340;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #F68B21 0%, transparent 100%);
  }
`;

const SettingsCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const SettingsHeader = styled.div`
  background: linear-gradient(135deg, #141340 0%, #1a1a4a 50%, #2d2d6b 100%);
  color: white;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(246, 139, 33, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const SettingsTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const SettingsSubtitle = styled.p`
  opacity: 0.9;
  margin-bottom: 0;
`;

const SettingsContent = styled.div`
  padding: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #141340;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f0f0f0;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #141340;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #F68B21;
    box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #F68B21;
    box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
  }
`;

const ToggleSwitch = styled.label`
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  margin-bottom: 1rem;
`;

const Switch = styled.div<{ isOn: boolean }>`
  width: 50px;
  height: 24px;
  background: ${({ isOn }) => (isOn ? '#F68B21' : '#ccc')};
  border-radius: 12px;
  position: relative;
  transition: all 0.3s ease;
  
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ isOn }) => (isOn ? '26px' : '2px')};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: all 0.3s ease;
  }
`;

const SwitchInput = styled.input`
  display: none;
`;

const ToggleLabel = styled.span`
  font-weight: 600;
  color: #141340;
`;

const ToggleDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.25rem;
  margin-left: 66px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(246, 139, 33, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover {
            background: #c82333;
            transform: translateY(-2px);
          }
        `;
      default:
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
    }
  }}
`;

const PasswordInputGroup = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.25rem;
  
  &:hover {
    color: #141340;
  }
`;

const HRSettings: React.FC = () => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    securityAlerts: true,
    twoFactorAuth: false,
    autoLogout: true,
    language: 'en',
    theme: 'light',
    timezone: 'Africa/Dar_es_Salaam'
  });

  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleToggle = (key: keyof typeof settings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleInputChange = (key: keyof typeof settings, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePasswordChange = (key: keyof typeof passwords, value: string) => {
    setPasswords(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const togglePasswordVisibility = (key: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <Container>
      <PageTitle>
        <IconWrapper icon={FaCog} />
        Settings
      </PageTitle>

      <SettingsCard>
        <SettingsHeader>
          <SettingsTitle>Account Settings</SettingsTitle>
          <SettingsSubtitle>Manage your account preferences and security settings</SettingsSubtitle>
        </SettingsHeader>

        <SettingsContent>
          <Section>
            <SectionTitle>
              <IconWrapper icon={FaBell} />
              Notification Settings
            </SectionTitle>
            
            <ToggleSwitch>
              <SwitchInput
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={() => handleToggle('emailNotifications')}
              />
              <Switch isOn={settings.emailNotifications} />
              <div>
                <ToggleLabel>Email Notifications</ToggleLabel>
                <ToggleDescription>Receive notifications via email</ToggleDescription>
              </div>
            </ToggleSwitch>

            <ToggleSwitch>
              <SwitchInput
                type="checkbox"
                checked={settings.smsNotifications}
                onChange={() => handleToggle('smsNotifications')}
              />
              <Switch isOn={settings.smsNotifications} />
              <div>
                <ToggleLabel>SMS Notifications</ToggleLabel>
                <ToggleDescription>Receive notifications via SMS</ToggleDescription>
              </div>
            </ToggleSwitch>

            <ToggleSwitch>
              <SwitchInput
                type="checkbox"
                checked={settings.securityAlerts}
                onChange={() => handleToggle('securityAlerts')}
              />
              <Switch isOn={settings.securityAlerts} />
              <div>
                <ToggleLabel>Security Alerts</ToggleLabel>
                <ToggleDescription>Get notified about security events</ToggleDescription>
              </div>
            </ToggleSwitch>
          </Section>

          <Section>
            <SectionTitle>
              <IconWrapper icon={FaShieldAlt} />
              Security Settings
            </SectionTitle>
            
            <ToggleSwitch>
              <SwitchInput
                type="checkbox"
                checked={settings.twoFactorAuth}
                onChange={() => handleToggle('twoFactorAuth')}
              />
              <Switch isOn={settings.twoFactorAuth} />
              <div>
                <ToggleLabel>Two-Factor Authentication</ToggleLabel>
                <ToggleDescription>Add an extra layer of security to your account</ToggleDescription>
              </div>
            </ToggleSwitch>

            <ToggleSwitch>
              <SwitchInput
                type="checkbox"
                checked={settings.autoLogout}
                onChange={() => handleToggle('autoLogout')}
              />
              <Switch isOn={settings.autoLogout} />
              <div>
                <ToggleLabel>Auto Logout</ToggleLabel>
                <ToggleDescription>Automatically log out after 30 minutes of inactivity</ToggleDescription>
              </div>
            </ToggleSwitch>

            <FormGroup>
              <Label>Language</Label>
              <Select
                value={settings.language}
                onChange={(e) => handleInputChange('language', e.target.value)}
              >
                <option value="en">English</option>
                <option value="sw">Swahili</option>
                <option value="fr">French</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Theme</Label>
              <Select
                value={settings.theme}
                onChange={(e) => handleInputChange('theme', e.target.value)}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="auto">Auto</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Timezone</Label>
              <Select
                value={settings.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
              >
                <option value="Africa/Dar_es_Salaam">Africa/Dar es Salaam</option>
                <option value="UTC">UTC</option>
                <option value="Africa/Nairobi">Africa/Nairobi</option>
              </Select>
            </FormGroup>
          </Section>

          <Section>
            <SectionTitle>
              <IconWrapper icon={FaKey} />
              Change Password
            </SectionTitle>
            
            <FormGroup>
              <Label>Current Password</Label>
              <PasswordInputGroup>
                <Input
                  type={showPasswords.current ? 'text' : 'password'}
                  placeholder="Enter current password"
                  value={passwords.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                />
                <PasswordToggle onClick={() => togglePasswordVisibility('current')}>
                  <IconWrapper icon={showPasswords.current ? FaEyeSlash : FaEye} />
                </PasswordToggle>
              </PasswordInputGroup>
            </FormGroup>

            <FormGroup>
              <Label>New Password</Label>
              <PasswordInputGroup>
                <Input
                  type={showPasswords.new ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={passwords.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                />
                <PasswordToggle onClick={() => togglePasswordVisibility('new')}>
                  <IconWrapper icon={showPasswords.new ? FaEyeSlash : FaEye} />
                </PasswordToggle>
              </PasswordInputGroup>
            </FormGroup>

            <FormGroup>
              <Label>Confirm New Password</Label>
              <PasswordInputGroup>
                <Input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={passwords.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                />
                <PasswordToggle onClick={() => togglePasswordVisibility('confirm')}>
                  <IconWrapper icon={showPasswords.confirm ? FaEyeSlash : FaEye} />
                </PasswordToggle>
              </PasswordInputGroup>
            </FormGroup>
          </Section>

          <ActionButtons>
            <Button variant="secondary">
              <IconWrapper icon={FaTimes} />
              Cancel
            </Button>
            <Button variant="primary">
              <IconWrapper icon={FaSave} />
              Save Settings
            </Button>
          </ActionButtons>
        </SettingsContent>
      </SettingsCard>
    </Container>
  );
};

export default HRSettings; 